<template>
  <div class="code-editor-wrap">
    <codemirror ref="cm" :value="value" :options="options" @input="onInput"></codemirror>
  </div>
</template>
<script>
import { codemirror } from 'vue-codemirror-lite'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/idea.css'
import 'codemirror/theme/eclipse.css'
import 'codemirror/theme/monokai.css'
import 'codemirror/theme/solarized.css'
// 编辑器增强功能
import 'codemirror/addon/selection/active-line'
import 'codemirror/addon/edit/matchbrackets'
import 'codemirror/addon/edit/closebrackets'
import 'codemirror/addon/edit/matchtags'
import 'codemirror/addon/edit/closetag'
import 'codemirror/addon/fold/foldgutter.css'
import 'codemirror/addon/fold/foldgutter'
import 'codemirror/addon/fold/brace-fold'
import 'codemirror/addon/fold/indent-fold'
import 'codemirror/addon/hint/show-hint.css'
import 'codemirror/addon/hint/show-hint'
import 'codemirror/addon/hint/anyword-hint'
// 各语言语法高亮
import 'codemirror/mode/clike/clike'
import 'codemirror/mode/python/python'
import 'codemirror/mode/pascal/pascal'
import 'codemirror/mode/go/go'
import 'codemirror/mode/d/d'
import 'codemirror/mode/haskell/haskell'
import 'codemirror/mode/mllike/mllike'
import 'codemirror/mode/perl/perl'
import 'codemirror/mode/php/php'
import 'codemirror/mode/ruby/ruby'
import 'codemirror/mode/rust/rust'
import 'codemirror/mode/javascript/javascript'
import 'codemirror/mode/fortran/fortran'

// contentType 到 CodeMirror mode 的映射，与后端 hoj_language.content_type 对应
const MODE_MAP = {
  c: 'text/x-csrc',
  cpp: 'text/x-c++src',
  csharp: 'text/x-csharp',
  java: 'text/x-java',
  python: 'text/x-python',
  python3: 'text/x-python',
  go: 'text/x-go',
  rust: 'text/x-rustsrc',
  php: 'application/x-httpd-php',
  ruby: 'text/x-ruby',
  perl: 'text/x-perl',
  pascal: 'text/x-pascal',
  javascript: 'text/javascript',
  haskell: 'text/x-haskell',
  d: 'text/x-d',
  ocaml: 'text/x-ocaml',
  fortran: 'text/x-fortran'
}

export default {
  name: 'CodeEditor',
  components: { codemirror },
  props: {
    // 编辑器内容，支持 v-model
    value: { type: String, default: '' },
    // 语言语法类型，取自题目可选语言的 contentType，如 cpp / python
    contentType: { type: String, default: 'cpp' },
    // 编辑器高度
    height: { type: String, default: '560px' },
    // 主题：idea 亮色 / monokai 暗色
    theme: { type: String, default: 'idea' },
    // 字号
    fontSize: { type: String, default: '14px' },
    // 缩进空格数
    tabSize: { type: Number, default: 4 },
    // 只读
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      options: {
        tabSize: this.tabSize,
        mode: this.resolveMode(this.contentType),
        theme: this.theme,
        lineNumbers: true,
        line: true,
        styleActiveLine: true,
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
        lineWrapping: true,
        showCursorWhenSelecting: true,
        matchBrackets: true,
        autoCloseBrackets: true,
        autoCloseTags: true,
        indentUnit: this.tabSize,
        hintOptions: { completeSingle: false },
        readOnly: this.readonly
      }
    }
  },
  watch: {
    value(val) {
      const cm = this.$refs.cm && this.$refs.cm.codemirror
      if (cm && cm.getValue() !== val) {
        // 外部赋值时同步到编辑器，避免光标跳动
        const cursor = cm.getCursor()
        cm.setValue(val == null ? '' : val)
        cm.setCursor(cursor)
      }
    },
    contentType(val) {
      const mode = this.resolveMode(val)
      this.options.mode = mode
      const cm = this.$refs.cm && this.$refs.cm.codemirror
      if (cm) {
        cm.setOption('mode', mode)
      }
    },
    theme(val) {
      const cm = this.$refs.cm && this.$refs.cm.codemirror
      if (cm) {
        cm.setOption('theme', val)
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      const cm = this.$refs.cm && this.$refs.cm.codemirror
      if (!cm) return
      cm.setSize('100%', this.height)
      // 输入字母时自动弹出提示
      cm.on('inputRead', (instance, change) => {
        const text = change.text && change.text[0]
        if (text && /[A-Za-z_]/.test(text.charAt(0))) {
          instance.showHint({ completeSingle: false })
        }
      })
    })
  },
  methods: {
    resolveMode(contentType) {
      if (!contentType) {
        return 'text/x-csrc'
      }
      // 已是完整 mode 名时直接返回
      if (contentType.indexOf('text/') === 0 || contentType.indexOf('application/') === 0) {
        return contentType
      }
      return MODE_MAP[contentType.toLowerCase()] || 'text/x-csrc'
    },
    onInput(content) {
      this.$emit('input', content)
    },
    // 供父组件主动获取内容
    getValue() {
      const cm = this.$refs.cm && this.$refs.cm.codemirror
      return cm ? cm.getValue() : this.value
    },
    // 供父组件主动设置内容
    setValue(val) {
      const cm = this.$refs.cm && this.$refs.cm.codemirror
      if (cm) {
        cm.setValue(val == null ? '' : val)
      }
    },
    // 刷新尺寸（父容器显隐切换后调用）
    refresh() {
      const cm = this.$refs.cm && this.$refs.cm.codemirror
      if (cm) {
        cm.refresh()
      }
    }
  }
}
</script>
<style scoped>
.code-editor-wrap {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}
.code-editor-wrap >>> .CodeMirror {
  font-size: 14px;
  height: auto;
  font-family: Consolas, Monaco, "Courier New", monospace;
}
.code-editor-wrap >>> .CodeMirror-gutters {
  border-right: 1px solid #ebeef5;
}
</style>
